import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRupeeSign,
  FaCarAlt,
  FaClock,
  FaPlay,
  FaCheck,
  FaStar,
} from "react-icons/fa";
import DriverLayout from "../../layouts/DriverLayout";
import {
  getCurrentBooking,
  startTrip,
  completeTrip,
  getDriverProfile,
  reset,
} from "../../features/driver/driverSlice";
import RatingModal from "../../components/shared/RatingModal";
import CODPaymentModal from "../../components/shared/CODPaymentModal";
import socketService from "../../utils/socketService";
import ratingService from "../../services/ratingService";
import axios from "axios";
import { getDriverToken, isDriverAuthenticated, getDriverAuthHeaders } from "../../utils/driverAuthUtils";

const CurrentRide = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { driver: driverAuth } = useSelector((state) => state.driverAuth);
  const { driver, currentBooking, loading, error, success, message } =
    useSelector((state) => state.driver);
  const [locationUpdateInterval, setLocationUpdateInterval] = useState(null);
  const [mapUrl, setMapUrl] = useState("");
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showCODPaymentModal, setShowCODPaymentModal] = useState(false);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [completedBooking, setCompletedBooking] = useState(null);
  const [realTimeStatus, setRealTimeStatus] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [showOtpInput, setShowOtpInput] = useState(false);

  // API URL for backend calls
  // Import API URL from config
  const { API_URL } = require("../../config/apiConfig");

  // Initialize socket connection - only once when component mounts
  useEffect(() => {
    let isActive = true;


    if (driver && driver._id) {
      // Connect to socket only once
      socketService.connect();

      // Join driver's room
      socketService.joinDriverRoom(driver._id);
    }

    // Cleanup function
    return () => {
      isActive = false;

      // Only cleanup location interval here, don't disconnect socket
      if (locationUpdateInterval) {
        clearInterval(locationUpdateInterval);
      }

    };
    // Empty dependency array ensures this only runs once when component mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure current booking loads even after refresh by falling back to localStorage
  useEffect(() => {
    const loadBooking = async () => {
      try {
        let driverId = driver?._id;
        if (!driverId) {
          const driverAuthState = { driver: driverAuth?.driver, token: driverAuth?.token };
          try {
            const { getDriverData } = require('../../utils/driverAuthUtils');
            const storedDriver = getDriverData(driverAuthState);
            driverId = storedDriver?._id;
          } catch {}
        }
        if (driverId) {
          dispatch(getCurrentBooking(driverId));
          dispatch(getDriverProfile(driverId));
        }
      } catch {}
    };
    loadBooking();
  }, [dispatch, driver?._id, driverAuth?.driver?._id]);

  // Set up socket event listeners separately (only once)
  useEffect(() => {
    if (driver && driver._id) {
      // Set up event listeners for socket events
      const bookingStatusListener = (data) => {
        if (
          currentBooking &&
          (data.bookingId === currentBooking.bookingId ||
            data._id === currentBooking._id)
        ) {
          toast.info(`Booking status updated to: ${data.status}`);
          setRealTimeStatus(data.status);
          dispatch(getCurrentBooking(driver._id)); // Refresh booking data
        }
      };

      const rideCompletedListener = (data) => {
        if (
          currentBooking &&
          (data.bookingId === currentBooking.bookingId ||
            data._id === currentBooking._id)
        ) {
          toast.success("Ride completed successfully!");
          setCompletedBooking(data);
          setShowRatingModal(true);
          dispatch(getCurrentBooking(driver._id)); // Refresh booking data
        }
      };

      const rideCancelledListener = (data) => {
        if (
          currentBooking &&
          (data.bookingId === currentBooking.bookingId ||
            data._id === currentBooking._id)
        ) {
          toast.info("This ride has been cancelled by the user");
          dispatch(getCurrentBooking(driver._id)); // Refresh booking data
        }
      };

      // Add event listeners
      socketService.onBookingStatusChanged(bookingStatusListener);
      socketService.onRideCompleted(rideCompletedListener);
      socketService.onRideCancelled(rideCancelledListener);

      // Cleanup function to remove the specific listeners we added
      return () => {
        // Just clean up state here
        dispatch(reset());
      };
    }
  }, [driver?._id, currentBooking, dispatch]);

  // Handle component unmount - only clean up event listeners, not the socket connection
  useEffect(() => {
    return () => {
      // We no longer disconnect the socket here - it's handled at the App level
      // Just clean up any intervals or timers
    };
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(reset());
    }

    if (success) {
      toast.success(message);
      dispatch(reset());
    }
  }, [error, success, message, dispatch]);

  useEffect(() => {
    if (currentBooking) {
      // Create Google Maps URL for directions will be set below

      // Create Google Maps URL for directions
      const pickupAddress = encodeURIComponent(currentBooking.pickupAddress);
      const dropAddress = encodeURIComponent(currentBooking.dropAddress);
      setMapUrl(
        `https://www.google.com/maps/dir/?api=1&origin=${pickupAddress}&destination=${dropAddress}&travelmode=driving`,
      );
    }
  }, [currentBooking]);

  const handleStartTrip = () => {

    if (currentBooking && driver) {
      // Check if OTP is required and provided
      if (currentBooking.startOtp && !otpInput) {
        setShowOtpInput(true);
        toast.warn("Please enter the trip start OTP");
        return;
      }

      dispatch(
        startTrip({
          driverId: driver._id,
          bookingId: currentBooking._id,
          otp: otpInput || undefined,
        }),
      ).then((result) => {
        // After trip start, refresh driver profile to ensure dashboard shows correct status
        if (result.type === "driver/startTrip/fulfilled") {
          // Refresh both driver profile and current booking to ensure populated user/passenger details
          dispatch(getDriverProfile(driver._id));
          dispatch(getCurrentBooking(driver._id));
          setOtpInput("");
          setShowOtpInput(false);
        }
      });
    } else {
      // Missing required data
    }
  };

  const handleCompleteTrip = () => {
    if (currentBooking && driver) {
      dispatch(
        completeTrip({
          driverId: driver._id,
          bookingId: currentBooking._id,
        }),
      ).then((result) => {
        // After trip completion, refresh driver profile to ensure dashboard shows correct status
        if (result.type === "driver/completeTrip/fulfilled") {
          dispatch(getDriverProfile(driver._id));

          // Force refresh after a short delay to ensure backend has updated
          setTimeout(() => {
            dispatch(getDriverProfile(driver._id));
          }, 1000);
        }
      });

      // Store the completed booking data
      setCompletedBooking(currentBooking);

      // Check if this is a COD booking
      if (
        currentBooking.paymentMethod === "COD" &&
        !currentBooking.isPaymentCollected
      ) {
        // Show COD payment collection modal first
        setTimeout(() => {
          setShowCODPaymentModal(true);
          toast.info("Please confirm payment collection from passenger");
        }, 1000);
      } else {
        // For online bookings or already collected COD, show rating modal directly
        setTimeout(() => {
          setShowRatingModal(true);
          toast.info("Please rate your passenger to complete the trip");
        }, 1000);
      }
    }
  };

  // Handle COD payment confirmation
  const handleCODPaymentConfirmation = async (paymentData) => {
    try {
      setIsSubmittingPayment(true);

      // Use utility function for authentication check
      if (!isDriverAuthenticated(driverAuth)) {
        console.error("Authentication failed - driver not properly authenticated");
        toast.error("You must be logged in as a driver to confirm payment");
        return;
      }

      const token = getDriverToken(driverAuth);

      if (!completedBooking) {
        toast.error("No booking information available");
        return;
      }


      // Debug API URL construction - API_URL already contains /api
      const fullUrl = `${API_URL}/booking/${completedBooking._id}/confirm-payment`;
      const authHeaders = getDriverAuthHeaders(driverAuth);


      // Call API to confirm payment collection
      const response = await axios.put(
        fullUrl,
        {
          paymentMethod: paymentData.method,
          amount: paymentData.amount,
          isPaymentCollected: true,
        },
        {
          headers: authHeaders,
        },
      );

      if (response.data.success) {
        toast.success("Payment confirmed successfully!");
        setShowCODPaymentModal(false);

        // Now show rating modal
        setTimeout(() => {
          setShowRatingModal(true);
          toast.info("Please rate your passenger to complete the trip");
        }, 500);
      } else {
        toast.error(response.data.message || "Failed to confirm payment");
      }
    } catch (error) {
      console.error("Error confirming payment:", error);
      toast.error(error.response?.data?.message || "Failed to confirm payment");
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  // Handle submission of passenger rating
  const handleSubmitRating = async (ratingData) => {
    try {
      setIsSubmittingRating(true);

      // Use utility function for authentication check
      if (!isDriverAuthenticated(driverAuth)) {
        console.error("Authentication failed - driver not properly authenticated for rating");
        toast.error("You must be logged in as a driver to submit a rating");
        return;
      }

      const authHeaders = getDriverAuthHeaders(driverAuth);

      if (!authHeaders) {
        toast.error("Authentication failed - please log in again");
        return;
      }

      if (!completedBooking || !completedBooking.user) {
        toast.error("No passenger information available to rate");
        return;
      }

      // Get user ID - handling both object references and string IDs
      const userId =
        typeof completedBooking.user === "object"
          ? completedBooking.user._id
          : completedBooking.user;


      // Use the rating service to submit the driver's rating for the user
      const response = await ratingService.submitDriverRating(
        completedBooking._id,
        userId,
        ratingData.rating,
        ratingData.comment,
        authHeaders,
      );

      if (response.success) {
        toast.success("Thank you for rating your passenger!");
        setShowRatingModal(false);
        // Refresh current booking to update UI
        dispatch(getCurrentBooking(driver._id));
      } else {
        toast.error(response.message || "Failed to submit rating");
      }
    } catch (error) {
      console.error("Error submitting rating:", error);
      toast.error(error.response?.data?.message || "Failed to submit rating");
    } finally {
      setIsSubmittingRating(false);
    }
  };

  if (loading && !currentBooking && !driver) {
    return (
      <DriverLayout>
        <div className="flex justify-center items-center h-96 page-container">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      <div className="container mx-auto px-4 py-6 page-container">
        <h1 className="text-2xl font-bold mb-6 mt-2">Current Ride</h1>

        {currentBooking ? (
          <div className="bg-white rounded-lg shadow-md overflow-hidden card-hover">
            <div className="bg-primary text-white p-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">
                  Booking #{currentBooking.bookingId}
                </h2>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${(realTimeStatus || currentBooking.status) === "assigned"
                    ? "bg-yellow-500"
                    : (realTimeStatus || currentBooking.status) ===
                      "inProgress"
                      ? "bg-blue-500"
                      : (realTimeStatus || currentBooking.status) ===
                        "completed"
                        ? "bg-green-500"
                        : (realTimeStatus || currentBooking.status) ===
                          "cancelled"
                          ? "bg-red-500"
                          : "bg-gray-500"
                    }`}
                >
                  {(realTimeStatus || currentBooking.status) === "assigned"
                    ? "Assigned"
                    : (realTimeStatus || currentBooking.status) === "inProgress"
                      ? "In Progress"
                      : (realTimeStatus || currentBooking.status) ===
                        "completed"
                        ? "Completed"
                        : (realTimeStatus || currentBooking.status) ===
                          "cancelled"
                          ? "Cancelled"
                          : currentBooking.status}
                </span>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 responsive-grid">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Trip Details</h3>

                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="mt-1 flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                          <FaMapMarkerAlt className="text-green-500" />
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-gray-500">Pickup Location</p>
                        <p className="font-medium">
                          {currentBooking.pickupLocation?.name ||
                            currentBooking.from ||
                            "Unknown Location"}
                        </p>
                        <p className="text-sm">
                          {currentBooking.pickupAddress}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="mt-1 flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                          <FaMapMarkerAlt className="text-red-500" />
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-gray-500">Drop Location</p>
                        <p className="font-medium">
                          {currentBooking.dropLocation?.name ||
                            currentBooking.to ||
                            "Unknown Location"}
                        </p>
                        <p className="text-sm">{currentBooking.dropAddress}</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="mt-1 flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <FaClock className="text-blue-500" />
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-gray-500">
                          Journey Time
                        </p>
                        <p className="font-medium">
                          {new Date(
                            currentBooking.pickupDate,
                          ).toLocaleDateString()}{" "}
                          at {currentBooking.pickupTime}
                        </p>
                        {currentBooking.journeyType === "roundTrip" && currentBooking.returnDate && (
                          <p className="text-sm text-gray-600 mt-1">
                            <span className="text-gray-500">Return:</span>{" "}
                            {new Date(currentBooking.returnDate).toLocaleDateString()}
                            {currentBooking.returnTime && ` at ${currentBooking.returnTime}`}
                          </p>
                        )}
                        {currentBooking.distance && currentBooking.distance > 0 && (
                          <p className="text-sm text-gray-600 mt-1">
                            <span className="text-gray-500">Distance:</span>{" "}
                            {currentBooking.distance} km
                          </p>
                        )}
                        {/* {currentBooking.duration && currentBooking.duration > 0 && (
                          <p className="text-sm text-gray-600">
                            <span className="text-gray-500">Duration:</span>{" "}
                            {Math.round(currentBooking.duration / 60)} min
                          </p>
                        )} */}
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="mt-1 flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <FaCarAlt className="text-purple-500" />
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-gray-500">Service Details</p>
                        <p className="font-medium">
                          {currentBooking.cabType?.name || "Standard"}
                        </p>
                        <div className="text-sm space-y-1 mt-1">
                          <p>
                            <span className="text-gray-500">Service:</span>{" "}
                            <span className="capitalize font-medium">
                              {currentBooking.serviceType === "rental" ? "Local Rental" :
                                currentBooking.serviceType === "airport" ? "Airport Transfer" :
                                  currentBooking.serviceType || "Outstation"}
                            </span>
                          </p>
                          {currentBooking.viaCities && currentBooking.viaCities.length > 0 && (
                            <p>
                              <span className="text-gray-500">Via:</span>{" "}
                              <span className="font-medium">
                                {currentBooking.viaCities.join(", ")}
                              </span>
                            </p>
                          )}
                          {currentBooking.serviceType === "rental" && (
                            <div>
                              {currentBooking.packageName && (
                                <p>
                                  <span className="text-gray-500">Package:</span>{" "}
                                  <span className="font-medium">{currentBooking.packageName}</span>
                                </p>
                              )}
                              {currentBooking.rentalHours && (
                                <p>
                                  <span className="text-gray-500">Duration:</span>{" "}
                                  <span className="font-medium">{currentBooking.rentalHours} hours</span>
                                </p>
                              )}
                              {currentBooking.rentalKm && (
                                <p>
                                  <span className="text-gray-500">Km Limit:</span>{" "}
                                  <span className="font-medium">{currentBooking.rentalKm} km</span>
                                </p>
                              )}
                            </div>
                          )}
                          {currentBooking.serviceType === "airport" && (
                            <div>
                              {currentBooking.airportCode && (
                                <p>
                                  <span className="text-gray-500">Airport:</span>{" "}
                                  <span className="font-medium">{currentBooking.airportCode}</span>
                                </p>
                              )}
                              {currentBooking.transferDirection && (
                                <p>
                                  <span className="text-gray-500">Transfer:</span>{" "}
                                  <span className="font-medium capitalize">
                                    {currentBooking.transferDirection === "to" ? "To Airport" : "From Airport"}
                                  </span>
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* OTP Section Removed - Driver should not see the OTP value */}

                    <div className="flex items-start">
                      <div className="mt-1 flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                          <FaRupeeSign className="text-yellow-500" />
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-gray-500">Trip Fare</p>
                        {currentBooking.totalAmount && currentBooking.totalAmount > 0 && (
                          <p className="font-medium text-lg">
                            ₹{currentBooking.totalAmount.toFixed(2)}
                          </p>
                        )}
                        {(!currentBooking.totalAmount || currentBooking.totalAmount <= 0) && (
                          <p className="text-gray-500 italic">Fare details not available</p>
                        )}
                        <div className="text-sm space-y-1 mt-1">
                          {currentBooking.baseAmount && currentBooking.baseAmount > 0 && (
                            <p>
                              <span className="text-gray-500">Base:</span>{" "}
                              <span>₹{currentBooking.baseAmount.toFixed(2)}</span>
                            </p>
                          )}
                          {currentBooking.taxAmount && currentBooking.taxAmount > 0 && (
                            <p>
                              <span className="text-gray-500">Tax:</span>{" "}
                              <span>₹{currentBooking.taxAmount.toFixed(2)}</span>
                            </p>
                          )}
                          {/* {currentBooking.couponDiscountAmount && currentBooking.couponDiscountAmount !== 0 && (
                            <p>
                              <span className="text-gray-500">Coupon Discount:</span>{" "}
                              <span className="text-green-600">
                                -₹{Number(currentBooking.couponDiscountAmount).toString()}
                              </span>
                              {currentBooking.couponCode && (
                                <span className="text-xs text-gray-400 ml-1">
                                  ({currentBooking.couponCode})
                                </span>
                              )}
                            </p>
                          )} */}



                          {/* {currentBooking.tollCharges && currentBooking.tollCharges > 0 && (
                            <p>
                              <span className="text-gray-500">Toll:</span>{" "}
                              <span>₹{currentBooking.tollCharges.toFixed(2)}</span>
                            </p>
                          )}
                          {currentBooking.nightCharges && currentBooking.nightCharges > 0 && (
                            <p>
                              <span className="text-gray-500">Night charges:</span>{" "}
                              <span>₹{currentBooking.nightCharges.toFixed(2)}</span>
                            </p>
                          )}
                          {currentBooking.driverAllowance && currentBooking.driverAllowance > 0 && (
                            <p>
                              <span className="text-gray-500">Driver allowance:</span>{" "}
                              <span>₹{currentBooking.driverAllowance.toFixed(2)}</span>
                            </p>
                          )} */}
                          <p className="border-t pt-1 mt-2">
                            <span className="text-gray-500">Payment:</span>{" "}
                            <span className="font-medium capitalize">
                              {currentBooking.paymentMethod === "COD" ? "Cash on Delivery" : "Online"}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Passenger Information
                  </h3>

                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <div className="flex items-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-medium">
                        {(() => {
                          const passengerName = currentBooking.user?.name || currentBooking.passengerDetails?.name;
                          return passengerName ? passengerName.charAt(0).toUpperCase() : "P";
                        })()}
                      </div>
                      <div className="ml-3">
                        <p className="font-medium">
                          {currentBooking.user?.name ||
                           currentBooking.passengerDetails?.name ||
                           "Passenger"}
                        </p>
                        <p className="text-sm text-gray-600">
                          {(() => {
                            const journeyType = currentBooking.journeyType || currentBooking.tripType;
                            const serviceType = currentBooking.serviceType;

                            // Check for rental first (both journeyType and serviceType)
                            if (journeyType === "rental" || serviceType === "rental") {
                              return "Local Rental";
                            }
                            // Check for airport services
                            if (journeyType === "airport_pickup" || journeyType === "airport_drop" || serviceType === "airport") {
                              return "Airport Transfer";
                            }
                            // Check standard journey types
                            if (journeyType === "oneWay" || journeyType === "one-way") {
                              return "One Way";
                            } else if (journeyType === "roundTrip" || journeyType === "round-trip") {
                              return "Round Trip";
                            } else if (journeyType === "multiCity" || currentBooking.viaCities?.length > 0) {
                              return "Multi-City";
                            } else if (journeyType === "outstation") {
                              return "Outstation";
                            } else {
                              return "One Way"; // Default
                            }
                          })()}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center mt-4">
                      <FaPhoneAlt className="text-gray-500" />
                      <a
                        href={`tel:${currentBooking.user?.phone || currentBooking.passengerDetails?.phone || ''}`}
                        className={`ml-2 ${(currentBooking.user?.phone || currentBooking.passengerDetails?.phone) ? 'text-primary hover:underline' : 'text-gray-500'}`}
                      >
                        {currentBooking.user?.phone ||
                         currentBooking.passengerDetails?.phone ||
                         "Phone not available"}
                      </a>
                    </div>

                  </div>

                  <div className="mb-4">
                    <a
                      href={mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full block text-center bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition duration-200"
                    >
                      Open Navigation
                    </a>
                  </div>

                  <div className="flex flex-col space-y-4 mt-4">
                    {(realTimeStatus || currentBooking.status) ===
                      "assigned" && (
                        <>
                          {/* OTP Input Field */}
                          {(showOtpInput || currentBooking.startOtp) && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Enter Trip Start OTP
                              </label>
                              <input
                                type="text"
                                value={otpInput}
                                onChange={(e) => setOtpInput(e.target.value)}
                                placeholder="Enter 4-digit OTP"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                maxLength="4"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Ask passenger for the 4-digit OTP to start the trip
                              </p>
                            </div>
                          )}

                          <button
                            onClick={handleStartTrip}
                            className="bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg flex items-center justify-center transition duration-200 btn-animated"
                          >
                            <FaPlay className="mr-2" />
                            Start Trip
                          </button>
                        </>
                      )}

                    {(realTimeStatus || currentBooking.status) ===
                      "inProgress" && (
                        <button
                          onClick={handleCompleteTrip}
                          className="bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg flex items-center justify-center transition duration-200 btn-animated"
                        >
                          <FaCheck className="mr-2" />
                          Complete Trip
                        </button>
                      )}

                    {(realTimeStatus || currentBooking.status) ===
                      "completed" &&
                      !currentBooking.driverRating && (
                        <button
                          onClick={() => setShowRatingModal(true)}
                          className="bg-yellow-500 hover:bg-yellow-600 text-white py-3 px-4 rounded-lg flex items-center justify-center transition duration-200 btn-animated"
                        >
                          <FaStar className="mr-2" />
                          Rate Passenger
                        </button>
                      )}
                  </div>
                </div>
              </div>

              {currentBooking.additionalNotes && (
                <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
                  <h4 className="font-medium text-yellow-800 mb-2">
                    Additional Notes
                  </h4>
                  <p className="text-yellow-700">
                    {currentBooking.additionalNotes}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FaCarAlt className="text-gray-400 text-3xl" />
            </div>
            <h2 className="text-xl font-semibold mb-2">No Active Ride</h2>
            <p className="text-gray-600 mb-4">
              You don't have any active ride at the moment.
            </p>
            <p className="text-gray-500">
              Please check back later or contact admin for assignment.
            </p>
          </div>
        )}

        {/* COD Payment Modal */}
        {completedBooking?._id && (
          <CODPaymentModal
            isOpen={showCODPaymentModal}
            onClose={() => setShowCODPaymentModal(false)}
            onConfirm={handleCODPaymentConfirmation}
            bookingDetails={completedBooking}
            totalAmount={completedBooking.totalAmount}
            isSubmitting={isSubmittingPayment}
          />
        )}

        {/* Rating Modal */}
        {completedBooking?._id && (
          <RatingModal
            isOpen={showRatingModal}
            onClose={() => setShowRatingModal(false)}
            onSubmit={handleSubmitRating}
            recipientName={
              completedBooking?.user?.name ||
              completedBooking?.passengerDetails?.name ||
              "Passenger"
            }
            recipientType="user"
            bookingId={completedBooking._id}
          />
        )}
      </div>
    </DriverLayout>
  );
};

export default CurrentRide;
